import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Stack,
  Typography,
} from '@mui/joy';
import {
  Phone as PhoneIcon,
  DeviceMobile as MobileIcon,
  Star as StarIcon,
  FloppyDisk as SaveIcon,
} from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import api from '../../lib/api';

interface MyNumber {
  id: number;
  phone_number_id: number;
  phone_number: string;
  friendly_name: string;
  is_default: boolean;
  can_send_sms: boolean;
  can_make_calls: boolean;
}

const formatPhone = (phone: string): string => {
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11 && digits[0] === '1') {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phone;
};

/**
 * Answers "is a number hooked up to me, and can I click-to-call?" — the two
 * things that decide whether the Call and Text buttons elsewhere in ApexOS
 * will actually work for this user.
 */
export const PhoneAndCallingCard: React.FC = () => {
  const [mobilePhone, setMobilePhone] = useState('');
  const [savedMobile, setSavedMobile] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'danger'; text: string } | null>(null);

  const { data: myNumbers, isLoading } = useQuery<MyNumber[]>({
    queryKey: ['my-phone-numbers'],
    queryFn: async () => {
      const response = await api.get<MyNumber[] | { results: MyNumber[] }>(
        '/api/inbox/phone-number-assignments/my-numbers/'
      );
      const data = response.data as any;
      return Array.isArray(data) ? data : data?.results ?? [];
    },
  });

  useEffect(() => {
    api.getCurrentUser()
      .then((response) => {
        const value = response.data?.mobile_phone || '';
        setMobilePhone(value);
        setSavedMobile(value);
      })
      .catch(() => {
        // Leave the field blank; saving still works.
      });
  }, []);

  const handleSaveMobile = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await api.updateCurrentUser({ mobile_phone: mobilePhone.trim() });
      setSavedMobile(mobilePhone.trim());
      setMessage({ type: 'success', text: 'Mobile number saved' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      setMessage({
        type: 'danger',
        text: error?.responseData?.mobile_phone?.[0]
          || error?.responseData?.detail
          || 'Failed to save mobile number',
      });
    } finally {
      setSaving(false);
    }
  };

  const numbers = myNumbers ?? [];

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography level="title-lg" sx={{ mb: 0.5 }}>
          Phone &amp; Calling
        </Typography>
        <Typography level="body-sm" sx={{ mb: 3 }}>
          The business number you send from, and the phone we ring when you place a call.
        </Typography>

        {message && (
          <Alert color={message.type} sx={{ mb: 2 }}>
            {message.text}
          </Alert>
        )}

        {/* Business numbers assigned to this user */}
        <Typography level="title-sm" sx={{ mb: 1 }}>
          Your business number
        </Typography>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size="sm" />
          </Box>
        ) : numbers.length > 0 ? (
          <Stack spacing={1} sx={{ mb: 3 }}>
            {numbers.map((number) => (
              <Card key={number.id} variant="outlined" size="sm">
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <PhoneIcon size={20} />
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" useFlexGap>
                      <Typography level="title-sm">
                        {formatPhone(number.phone_number)}
                      </Typography>
                      {number.is_default && (
                        <Chip
                          size="sm"
                          variant="soft"
                          color="warning"
                          startDecorator={<StarIcon size={12} weight="fill" />}
                        >
                          Default
                        </Chip>
                      )}
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                      {number.friendly_name && (
                        <Typography level="body-xs" color="neutral">
                          {number.friendly_name}
                        </Typography>
                      )}
                      {number.can_send_sms && (
                        <Chip size="sm" variant="soft" color="success">SMS</Chip>
                      )}
                      {number.can_make_calls && (
                        <Chip size="sm" variant="soft" color="primary">Calls</Chip>
                      )}
                    </Stack>
                  </Box>
                </Stack>
              </Card>
            ))}
          </Stack>
        ) : (
          <Alert variant="soft" color="warning" sx={{ mb: 3 }}>
            No business number is assigned to you yet, so texting and calling from ApexOS
            won't work. Ask an organization admin to assign you one under
            Settings → Communications.
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Personal mobile used for the click-to-call bridge */}
        <Typography level="title-sm" sx={{ mb: 1 }}>
          Your mobile number
        </Typography>

        {!savedMobile && (
          <Alert variant="soft" color="warning" sx={{ mb: 2 }}>
            Add your mobile number to turn on click-to-call. Without it, the Call button
            on a contact has no phone to ring.
          </Alert>
        )}

        <FormControl>
          <FormLabel>Mobile number</FormLabel>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Input
              placeholder="e.g. +1 (832) 555-0184"
              value={mobilePhone}
              onChange={(event) => setMobilePhone(event.target.value)}
              startDecorator={<MobileIcon size={18} />}
              sx={{ flex: 1 }}
            />
            <Button
              variant="solid"
              startDecorator={<SaveIcon />}
              onClick={handleSaveMobile}
              loading={saving}
              disabled={saving || mobilePhone.trim() === savedMobile}
            >
              Save
            </Button>
          </Stack>
          <FormHelperText>
            When you click Call on a contact, ApexOS rings this phone first, then connects
            you to the customer — they see your business number, not this one.
          </FormHelperText>
        </FormControl>
      </CardContent>
    </Card>
  );
};

export default PhoneAndCallingCard;
